// Sistema de tipografías configurables (migration 018).
//
// 3 slots semánticos: heading (títulos), body (texto general),
// display (texto promocional). Cada slot acepta CUALQUIER family
// de la lista curada FONT_OPTIONS, agnóstica entre slots — la misma
// fuente puede usarse para más de uno si el admin lo quiere.
//
// Carga: las fuentes vienen de Google Fonts. Las inyectamos como
// <link> en el <head> on-demand cuando se necesitan, así no
// bajamos las 15 si solo se usan 1-3. El navegador cachea los
// woff2 por dominio (fonts.gstatic.com), así que cambiar de una
// fuente a otra entre sucursales no recarga lo que ya tiene.

export type FontCategory =
  | "sans-clean" // Sans-serif legibles para body/UI
  | "sans-display" // Sans-serif compactas/altas para impacto
  | "display" // Fuentes con personalidad fuerte (chunky, geometric)
  | "script" // Cursivas y handwritten festivas
  | "serif"; // Serif elegantes

export type FontOption = {
  family: string; // Nombre exacto como lo conoce Google Fonts
  label: string; // Etiqueta amigable para el admin
  category: FontCategory;
  // Weights que cargamos. Mantenemos el set chico para no inflar el
  // bundle de fonts. Si una fuente necesita más weights, sumarlos
  // acá. La mayoría de Google Fonts soporta 400 (normal) y 700 (bold).
  weights: number[];
  // Si la fuente tiene un sample text que la luce mejor (ej. Bebas
  // Neue es solo mayúsculas), va acá. Sino, el preview usa "GIRAR".
  sample?: string;
};

// Lista curada — orden = grupos por categoría. Sumar fuentes acá +
// nada más; el admin las renderiza automáticamente, el loader las
// importa, y los componentes leen las CSS vars correspondientes.
export const FONT_OPTIONS: FontOption[] = [
  // Sans-serif limpias — buenas para body y UI
  { family: "Nunito", label: "Nunito (default)", category: "sans-clean", weights: [400, 700, 900] },
  { family: "Inter", label: "Inter", category: "sans-clean", weights: [400, 600, 800] },
  { family: "Poppins", label: "Poppins", category: "sans-clean", weights: [400, 600, 800] },
  { family: "Roboto", label: "Roboto", category: "sans-clean", weights: [400, 700, 900] },
  { family: "Montserrat", label: "Montserrat", category: "sans-clean", weights: [400, 700, 900] },

  // Sans-serif display — compactas, impactantes
  { family: "Bebas Neue", label: "Bebas Neue", category: "sans-display", weights: [400], sample: "GIRAR PREMIO" },
  { family: "Oswald", label: "Oswald", category: "sans-display", weights: [400, 700] },
  { family: "Anton", label: "Anton", category: "sans-display", weights: [400] },

  // Display con personalidad
  { family: "Bungee", label: "Bungee", category: "display", weights: [400] },
  { family: "Russo One", label: "Russo One", category: "display", weights: [400] },
  { family: "Permanent Marker", label: "Permanent Marker", category: "display", weights: [400] },

  // Script — festivas
  { family: "Lobster", label: "Lobster", category: "script", weights: [400] },
  { family: "Pacifico", label: "Pacifico", category: "script", weights: [400] },

  // Serif elegantes
  { family: "Playfair Display", label: "Playfair Display", category: "serif", weights: [400, 700, 900] },
  { family: "Merriweather", label: "Merriweather", category: "serif", weights: [400, 700, 900] },
];

const FONT_BY_FAMILY = new Map(FONT_OPTIONS.map((f) => [f.family, f]));

export function getFontOption(family: string): FontOption | undefined {
  return FONT_BY_FAMILY.get(family);
}

export const FONT_CATEGORY_LABEL: Record<FontCategory, string> = {
  "sans-clean": "Sans-serif limpias",
  "sans-display": "Sans-serif compactas",
  display: "Display con personalidad",
  script: "Script / cursivas",
  serif: "Serif elegantes",
};

// ──────────────────────────────────────────────
// Loader: inyecta <link> al Google Fonts CSS API. Idempotente —
// múltiples llamadas con las mismas families no agregan links extra.
// ──────────────────────────────────────────────

const LINK_ID_PREFIX = "gfont-";

function familyToParam(opt: FontOption): string {
  // Google Fonts CSS2 API: "Family+Name:wght@400;700"
  const name = opt.family.replace(/ /g, "+");
  if (opt.weights.length === 0) return name;
  return `${name}:wght@${opt.weights.join(";")}`;
}

function ensureLinkLoaded(family: string): void {
  const opt = FONT_BY_FAMILY.get(family);
  if (!opt) return; // family desconocida → ignoramos (fallback CSS al stack default)
  const id = `${LINK_ID_PREFIX}${opt.family.replace(/ /g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${familyToParam(opt)}&display=swap`;
  document.head.appendChild(link);
}

// Carga TODAS las fuentes de FONT_OPTIONS. Útil para el selector del
// admin: necesitamos que cada opción se vea en su propia tipografía,
// así que todas tienen que estar disponibles. Idempotente (los links
// ya cargados no se duplican).
export function preloadAllFontOptions(): void {
  for (const opt of FONT_OPTIONS) {
    ensureLinkLoaded(opt.family);
  }
}

// Aplica las 3 fuentes seleccionadas: carga las families necesarias
// y setea las CSS vars en :root para que los componentes (que ya
// referencian var(--font-heading), etc.) las usen.
export function applyFonts(fonts: {
  heading: string;
  body: string;
  display: string;
}): void {
  // Cargar links (de-duplicados naturalmente por el ID check).
  ensureLinkLoaded(fonts.heading);
  ensureLinkLoaded(fonts.body);
  ensureLinkLoaded(fonts.display);

  // Setear CSS vars. Cada una incluye un fallback al stack default
  // por si la fuente Google todavía no terminó de descargarse o si
  // el family no está en FONT_OPTIONS (graceful degradation).
  const root = document.documentElement;
  root.style.setProperty("--font-heading", `"${fonts.heading}", "Nunito", system-ui, sans-serif`);
  root.style.setProperty("--font-body", `"${fonts.body}", "Nunito", system-ui, sans-serif`);
  root.style.setProperty("--font-display", `"${fonts.display}", "Nunito", system-ui, sans-serif`);
  // Invalida la cache de family del canvas: el proximo getCanvasFont vuelve
  // a resolver la family nueva (una sola vez).
  clearCanvasFontCache();
}

// Cache de la family resuelta por slot para el canvas. getComputedStyle es
// CARO (fuerza un style/layout recalc sincrono del documento entero) y el
// wheel llamaba getCanvasFont por gajo y por frame -> cientos de recalcs
// forzados por segundo que tiraban los FPS y se veia "lentisima" en kioskos
// modestos. Cacheamos la family resuelta; applyFonts() invalida la cache
// cuando el admin cambia la tipografia.
const canvasFamilyCache: Partial<Record<"heading" | "body" | "display", string>> = {};

export function clearCanvasFontCache(): void {
  canvasFamilyCache.heading = undefined;
  canvasFamilyCache.body = undefined;
  canvasFamilyCache.display = undefined;
}

function getCanvasFontFamily(slot: "heading" | "body" | "display"): string {
  const cached = canvasFamilyCache[slot];
  if (cached) return cached;
  const css = getComputedStyle(document.documentElement).getPropertyValue(`--font-${slot}`);
  // css viene como '"Family Name", "Nunito", ...'. Extraemos la 1ra.
  const first = css.split(",")[0]?.trim().replace(/^"|"$/g, "") ?? "Nunito";
  canvasFamilyCache[slot] = first;
  return first;
}

// Helper para el canvas del wheel (que NO puede leer CSS vars
// directamente — necesita la family literal). Usa la family CACHEADA, sin
// getComputedStyle en el hot path del giro.
export function getCanvasFont(slot: "heading" | "body" | "display", weight: number, sizePx: number): string {
  return `${weight} ${sizePx}px "${getCanvasFontFamily(slot)}", "Nunito", sans-serif`;
}
